<script setup>
import { ref, onMounted } from 'vue';

// Zustände
const users = ref([]);
const newUser = ref({
  username: '',
  password: '',
  email: ''
});
const editUser = ref(null);
const showAddForm = ref(false);
const showEditForm = ref(false);
const loading = ref(false);
const error = ref(null);
const successMessage = ref(null);

// Formular zurücksetzen
const resetForm = () => {
  newUser.value = {
    username: '',
    password: '',
    email: ''
  };
  editUser.value = null;
  showAddForm.value = false;
  showEditForm.value = false;
};

// Administratoren laden
const loadUsers = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetch('/api/admins.php');
    const data = await response.json();
    
    if (data.success) {
      users.value = data.admins;
    } else {
      throw new Error(data.error || 'Fehler beim Laden der Administratoren');
    }
  } catch (err) {
    console.error('Fehler beim Laden der Administratoren:', err);
    error.value = err.message || 'Ein Fehler ist aufgetreten';
  } finally {
    loading.value = false;
  }
};

// Neuen Administrator erstellen
const createUser = async () => {
  if (!validateForm()) return;
  
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetch('/api/admins.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newUser.value)
    });
    
    const data = await response.json();
    
    if (data.success) {
      await loadUsers();
      resetForm();
      showSuccessMessage('Administrator wurde erfolgreich erstellt');
    } else {
      throw new Error(data.error || 'Fehler beim Erstellen des Administrators');
    }
  } catch (err) {
    console.error('Fehler beim Erstellen des Administrators:', err);
    error.value = err.message || 'Ein Fehler ist aufgetreten';
  } finally {
    loading.value = false;
  }
};

// Administrator zum Bearbeiten auswählen
const selectUserForEdit = (user) => {
  editUser.value = {
    ...user,
    password: '' // Passwort-Feld wird zurückgesetzt, damit es nur bei Bedarf geändert wird
  };
  showEditForm.value = true;
};

// Administrator aktualisieren
const updateUser = async () => {
  if (!editUser.value) return;
  
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetch('/api/admins.php', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(editUser.value)
    });
    
    const data = await response.json();
    
    if (data.success) {
      await loadUsers();
      resetForm();
      showSuccessMessage('Administrator wurde erfolgreich aktualisiert');
    } else {
      throw new Error(data.error || 'Fehler beim Aktualisieren des Administrators');
    }
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Administrators:', err);
    error.value = err.message || 'Ein Fehler ist aufgetreten';
  } finally {
    loading.value = false;
  }
};

// Administrator löschen
const deleteUser = async (user) => {
  if (!confirm(`Möchten Sie den Administrator "${user.username}" wirklich löschen?`)) {
    return;
  }
  
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetch('/api/admins.php', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: user.id })
    });
    
    const data = await response.json();
    
    if (data.success) {
      await loadUsers();
      showSuccessMessage('Administrator wurde erfolgreich gelöscht');
    } else {
      throw new Error(data.error || 'Fehler beim Löschen des Administrators');
    }
  } catch (err) {
    console.error('Fehler beim Löschen des Administrators:', err);
    error.value = err.message || 'Ein Fehler ist aufgetreten';
  } finally {
    loading.value = false;
  }
};

// Formular validieren
const validateForm = () => {
  if (newUser.value.username.trim() === '') {
    error.value = 'Benutzername ist erforderlich';
    return false;
  }
  
  if (newUser.value.password.trim() === '') {
    error.value = 'Passwort ist erforderlich';
    return false;
  }
  
  return true;
};

// Erfolgs-Nachricht anzeigen und nach 5 Sekunden ausblenden
const showSuccessMessage = (message) => {
  successMessage.value = message;
  setTimeout(() => {
    successMessage.value = null;
  }, 5000);
};

// Datum formatieren
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Letztes Login formatieren
const formatLastLogin = (dateString) => {
  if (!dateString || dateString === '0000-00-00 00:00:00' || dateString === 'null') {
    return 'Noch nicht eingeloggt';
  }
  return formatDate(dateString);
};

// Beim Laden der Komponente die Administratoren abrufen
onMounted(() => {
  loadUsers();
});
</script>

<template>
  <div class="users-manager">
    <div class="section-header">
      <h2>Benutzerverwaltung</h2>
      <p class="section-description">
        Hier können Sie Administratoren verwalten, die Zugriff auf das Admin-Panel haben.
      </p>
    </div>
    
    <!-- Bedienelemente -->
    <div class="controls">
      <button 
        v-if="!showAddForm" 
        @click="showAddForm = true" 
        class="btn-add"
        :disabled="loading"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8.5" cy="7" r="4"></circle>
          <line x1="20" y1="8" x2="20" y2="14"></line>
          <line x1="23" y1="11" x2="17" y2="11"></line>
        </svg>
        Neuen Administrator hinzufügen
      </button>
    </div>
    
    <!-- Formular zum Hinzufügen eines neuen Administrators -->
    <div v-if="showAddForm" class="form-card">
      <div class="card-header">
        <h3>Neuen Administrator erstellen</h3>
      </div>
      
      <form @submit.prevent="createUser" class="user-form">
        <div class="form-group">
          <label for="username">Benutzername*</label>
          <input
            type="text"
            id="username"
            v-model="newUser.username"
            required
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="password">Passwort*</label>
          <input
            type="password"
            id="password"
            v-model="newUser.password"
            required
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="email">E-Mail</label>
          <input
            type="email"
            id="email"
            v-model="newUser.email"
            class="form-control"
          />
        </div>
        
        <div class="form-actions">
          <button type="button" @click="resetForm" class="btn-cancel">Abbrechen</button>
          <button type="submit" class="btn-save" :disabled="loading">Speichern</button>
        </div>
      </form>
    </div>
    
    <!-- Formular zum Bearbeiten eines Administrators -->
    <div v-if="showEditForm && editUser" class="form-card">
      <div class="card-header">
        <h3>Administrator bearbeiten</h3>
      </div>
      
      <form @submit.prevent="updateUser" class="user-form">
        <div class="form-group">
          <label for="edit-username">Benutzername*</label>
          <input
            type="text"
            id="edit-username"
            v-model="editUser.username"
            required
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="edit-password">Passwort (leer lassen, um nicht zu ändern)</label>
          <input
            type="password"
            id="edit-password"
            v-model="editUser.password"
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="edit-email">E-Mail</label>
          <input
            type="email"
            id="edit-email"
            v-model="editUser.email"
            class="form-control"
          />
        </div>
        
        <div class="form-actions">
          <button type="button" @click="resetForm" class="btn-cancel">Abbrechen</button>
          <button type="submit" class="btn-save" :disabled="loading">Aktualisieren</button>
        </div>
      </form>
    </div>
    
    <!-- Administratoren-Liste -->
    <div class="user-list-container">
      <div v-if="loading && !users.length" class="loading-container">
        <div class="loading-spinner"></div>
        <span>Administratoren werden geladen...</span>
      </div>
      
      <div v-else-if="!users.length" class="empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        <p>Keine Administratoren gefunden.</p>
      </div>
      
      <table v-else class="user-table">
        <thead>
          <tr>
            <th>Benutzername</th>
            <th>E-Mail</th>
            <th>Erstellt am</th>
            <th>Letzter Login</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.username }}</td>
            <td>{{ user.email || '-' }}</td>
            <td>{{ formatDate(user.created_at) }}</td>
            <td>{{ formatLastLogin(user.last_login) }}</td>
            <td class="actions">
              <button 
                @click="selectUserForEdit(user)" 
                class="btn-icon btn-edit"
                :disabled="loading"
                :title="`${user.username} bearbeiten`"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button 
                v-if="user.username !== 'admin'"
                @click="deleteUser(user)" 
                class="btn-icon btn-delete"
                :disabled="loading"
                :title="`${user.username} löschen`"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Nachrichten -->
    <div v-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ error }}</span>
    </div>
    
    <div v-if="successMessage" class="success-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
    
    <div v-if="loading && users.length" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>
  </div>
</template>

<style scoped>
.users-manager {
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.section-description {
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.5;
}

.controls {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.65rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-add:hover {
  background-color: var(--primary-color-dark);
}

.form-card {
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.card-header {
  margin-bottom: 1.5rem;
}

.card-header h3 {
  font-size: 1.2rem;
  color: var(--text-color);
  margin: 0;
}

.user-form {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-group {
  margin-bottom: 0.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-control {
  width: 100%;
  padding: 0.65rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.btn-save, .btn-cancel {
  padding: 0.65rem 1.25rem;
  border-radius: 4px;
  border: none;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-normal);
}

.btn-save {
  background-color: var(--primary-color);
  color: white;
}

.btn-save:hover {
  background-color: var(--primary-color-dark);
}

.btn-cancel {
  background-color: #f1f1f1;
  color: var(--text-color);
}

.btn-cancel:hover {
  background-color: #e0e0e0;
}

.user-list-container {
  margin-top: 2rem;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.user-table th, .user-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.user-table th {
  background-color: #f9f9f9;
  font-weight: 600;
  color: var(--text-color);
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-edit {
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
}

.btn-edit:hover {
  background-color: rgba(0, 132, 61, 0.2);
}

.btn-delete {
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

.btn-delete:hover {
  background-color: rgba(244, 67, 54, 0.2);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--white);
  border-radius: 8px;
  padding: 3rem;
  color: var(--text-muted);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin-bottom: 1rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message, .success-message {
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 4px;
}

.error-message {
  background-color: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
}

.success-message {
  background-color: rgba(76, 175, 80, 0.1);
  color: #388e3c;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .user-table {
    display: block;
    overflow-x: auto;
  }
  
  .user-form {
    grid-template-columns: 1fr;
  }
}
</style> 